import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { api, formatApiErrorDetail } from "../lib/api";
import { Car, ShieldAlert } from "lucide-react";

export default function Login() {
  const nav = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [blocked, setBlocked] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await login(email, password);
      nav("/dashboard");
    } catch (ex) {
      const d = ex?.response?.data?.detail;
      if (d?.code === "login_confirmation_required") { setBlocked(d); return; }
      setErr(formatApiErrorDetail(d) || ex.message);
    } finally {
      setBusy(false);
    }
  };

  const SocialButtons = () => {
    const [s, setS] = useState(null);
    useEffect(() => { api.get("/auth/social/status").then((r) => setS(r.data)).catch(() => setS({})); }, []);
    if (!s || (!s.facebook && !s.apple)) return null;
    return <div className="grid grid-cols-2 gap-2 -mt-3 mb-5">
      {s.facebook && <Button type="button" onClick={() => { window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/facebook/start`; }} data-testid="facebook-login-btn" className="h-11 bg-[#1877F2] hover:bg-[#166FE5] text-white font-medium">Facebook</Button>}
      {s.apple && <Button type="button" onClick={() => { window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/apple/start`; }} data-testid="apple-login-btn" className="h-11 bg-white hover:bg-slate-100 text-black font-medium"> Apple</Button>}
    </div>;
  };
  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirect = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  };

  return (
    <main className="min-h-[calc(100vh-5rem)] flex items-center justify-center px-4 py-12" data-testid="login-page">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center">
            <Car className="w-5 h-5 text-slate-950" strokeWidth={2.5} />
          </div>
          <span className="font-display font-extrabold text-xl">MY VELOCITY</span>
        </Link>

        <div className="glass rounded-3xl p-8 rise">
          <h1 className="font-display text-3xl font-bold mb-1">Welcome back</h1>
          <p className="text-slate-400 text-sm mb-6">Log in to manage your trips & cars.</p>

          <Button type="button" onClick={google} data-testid="google-login-btn"
            className="w-full h-11 mb-5 bg-slate-900 hover:bg-slate-800 border border-slate-700 text-white font-medium">
            <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.83z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.83C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
            Continue with Google
          </Button>
          <SocialButtons />
          <div className="relative my-5 text-center">
            <span className="relative z-10 px-3 bg-[#131722] text-xs font-mono uppercase tracking-widest text-slate-500">or</span>
            <div className="absolute inset-x-0 top-1/2 border-t border-slate-800" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div><Label className="text-slate-300">Email</Label><Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" className="bg-slate-900 border-slate-800 mt-1.5 h-11" /></div>
            <div><Label className="text-slate-300">Password</Label><Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" className="bg-slate-900 border-slate-800 mt-1.5 h-11" /></div>
            {err && <p className="text-sm text-red-400" data-testid="login-error">{err}</p>}
            {blocked && <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-400/40 text-sm text-amber-100 flex gap-2" data-testid="login-blocked-new-country"><ShieldAlert className="w-4 h-4 mt-0.5 shrink-0 text-amber-300" /><span>{blocked.message}</span></div>}
            <Button type="submit" disabled={busy} data-testid="login-submit" className="w-full h-11 bg-white text-slate-950 hover:bg-slate-100 font-semibold">
              {busy ? "Signing in…" : "Sign in"}
            </Button>
          </form>
          <div className="mt-5 flex justify-between text-sm">
            <Link to="/forgot-password" className="text-slate-400 hover:text-white" data-testid="link-forgot">Forgot password?</Link>
            <Link to="/register" className="text-cyan-400 hover:text-cyan-300" data-testid="link-register">Create account</Link>
          </div>
        </div>
      </div>
    </main>
  );
}
